/**
 * This file has all Storefront API calls. The functions in this file are used to fetch data from the Storefront API.
 * The access token is stored in shopify settings and it's an app name YLH Storefront
 * The query is pretty complex and uses fragments and variables to fetch the data. The query is written in GraphQL.
 * The functions are async and return a promise.
 */
const productsData = {};
const country = Shopify.country;

const fetchProductData = async (handle) => {
  if (productsData[handle]) {
    return productsData[handle];
  }

  const PRODUCT_DATA_QUERY = /* GraphQL */ `
    query getProductByHandle($handle: String!, $country: CountryCode!) @inContext(country: $country) {
      productByHandle(handle: $handle) {
        id
        title
        handle
        productType
        description
        availableForSale
        options {
          name
          values
        }
        variants(first: 250) {
          edges {
            node {
              id
              title
              availableForSale
              image {
                url
              }
              continueSelling: metafield(namespace: "custom", key: "continue_selling") {
                value
              }
              quantityAvailable
              disableSwatch: metafield(namespace: "prc", key: "disable_swatch") {
                value
              }
              variantSubtext: metafield(namespace: "custom", key: "variant_subtext") {
                value
              }
              variantOffPrice: metafield(namespace: "custom", key: "off_price") {
                value
              }
              variantSelectorImage: metafield(namespace: "custom", key: "variant_selector_image") {
                reference {
                  ... on MediaImage {
                    image {
                      originalSrc
                    }
                  }
                }
              }
              standaloneVariant: metafield(namespace: "custom", key: "standalone_variant") {
                value
              }
              variantProperties: metafield(namespace: "custom", key: "variant_properties") {
                reference {
                  ... on Metaobject {
                    fields {
                      key
                      value
                    }
                  }
                }
              }
              selectedOptions {
                name
                value
              }
              priceV2 {
                amount
                currencyCode
              }
              compareAtPriceV2 {
                amount
                currencyCode
              }
            }
          }
        }
        media(first: 30) {
          edges {
            node {
              ... on MediaImage {
                id
                image {
                  originalSrc
                  altText
                }
              }
              ... on Video {
                id
                sources {
                  url
                }
                previewImage {
                  originalSrc
                  altText
                }
              }
              ... on Model3d {
                id
                sources {
                  url
                }
                previewImage {
                  originalSrc
                  altText
                }
              }
            }
          }
        }
        tags
        swatchImage: metafield(namespace: "custom", key: "swatch_image_reference") {
          value
        }
        swatchOrder: metafield(namespace: "custom", key: "swatch_order") {
          value
        }
        disableSwatch: metafield(namespace: "prc", key: "disable_swatch") {
          value
        }
        shortDescription: metafield(namespace: "custom", key: "product_card_description") {
          value
        }
        productOffPrice: metafield(namespace: "custom", key: "off_price_product") {
          value
        }
        bundleHandle: metafield(namespace: "custom", key: "pdp_bundle_handle") {
          value
        }
        backStock: metafield(namespace: "custom", key: "back_in_stock") {
          value
        }
        swatchBadge: metafield(namespace: "custom", key: "swatch_badge") {
          value
          reference {
            ... on Metaobject {
              fields {
                key
                value
              }
            }
          }
        }
        productAccordion: metafield(namespace: "custom", key: "pdp_accordion") {
          references(first: 10) {
            nodes {
              ... on Metaobject {
                fields {
                  key
                  value
                }
              }
            }
          }
        }
        multiSwatch: metafield(namespace: "custom", key: "multiple_swatch_options") {
          references(first: 250) {
            nodes {
              ... on Metaobject {
                fields {
                  key
                  value
                }
              }
            }
          }
        }
        optionProps: metafield(namespace: "custom", key: "option_selector_properties") {
          references(first: 250) {
            nodes {
              ... on Metaobject {
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: PRODUCT_DATA_QUERY,
        variables: {
          handle: handle,
          country: country,
        },
      }),
    });
    const jsonResponse = await response.json();
    const product = jsonResponse.data.productByHandle;
    const mediaObjects = product.media.edges.map(({ node }) => {
      let mediaItem = {
        id: node.id,
        type: '',
        src: '',
        thumbnail: '',
        alt: node.image ? node.image.altText || product.title : product.title,
        sources: [],
      };

      if (node.image) {
        mediaItem.type = 'image';
        mediaItem.src = node.image.originalSrc;
        mediaItem.thumbnail = node.image.originalSrc;
      } else if (node.sources) {
        mediaItem.type = node.sources.length > 0 ? 'Video' : 'Model3d';
        mediaItem.src = node.sources[0].url;
        mediaItem.thumbnail = node.previewImage ? node.previewImage.originalSrc : node.sources[0].url;
        mediaItem.sources = node.sources.map((source) => source.url);
      }

      return mediaItem;
    });

    const PDiscountValue = product.productOffPrice?.value * window.Shopify.currency.rate;

    const variants = product.variants.edges.map((edge) => {
      const variantNode = edge.node;

      const VDiscountValue = variantNode.variantOffPrice?.value * window.Shopify.currency.rate;
      const moneyDiscountValue = isNaN(VDiscountValue) != true ? VDiscountValue : PDiscountValue;

      const moneyDiscountValueTR = VDiscountValue != 0 ? true : false;
      
      let variantPropertiesObject = null;
      if (variantNode.variantProperties) {
        variantPropertiesObject = {};
        variantNode.variantProperties.reference.fields.forEach(({ key, value }) => {
          variantPropertiesObject[key] = value;
        });
      }
    
        return {
          id: variantNode.id.split('/').pop(),
          disableSwatch: variantNode.disableSwatch?.value,
          title: variantNode.title,
          variantSubtext: variantNode.variantSubtext,
          image: variantNode.image?.url,
          variantSelectorImage: variantNode.variantSelectorImage
            ? variantNode.variantSelectorImage?.reference.image.originalSrc
            : null,
          availableForSale: variantNode.availableForSale,
          selectedOptions: variantNode.selectedOptions.map((option) => ({
            name: option.name,
            value: option.value,
          })),

          price: isNaN(moneyDiscountValue) != true ? variantNode.priceV2.amount - moneyDiscountValue : variantNode.priceV2.amount,
          compareAtPrice: moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? variantNode.priceV2.amount : variantNode.compareAtPriceV2 ? variantNode.compareAtPriceV2.amount : null,
          hasReducedPrice: moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? true
                : variantNode.compareAtPriceV2 && variantNode.compareAtPriceV2.amount > variantNode.priceV2.amount ? true : false,
          formattedPrice: isNaN(moneyDiscountValue) != true ? formatPrice(variantNode.priceV2.currencyCode, variantNode.priceV2.amount - moneyDiscountValue)
                : formatPrice(variantNode.priceV2.currencyCode, variantNode.priceV2.amount),
          formattedCompareAtPrice: moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? formatPrice(variantNode.priceV2.currencyCode, variantNode.priceV2.amount)
                : variantNode.compareAtPriceV2 ? formatPrice(variantNode.compareAtPriceV2.currencyCode, variantNode.compareAtPriceV2.amount) : null,
          
          quantityAvailable: variantNode.quantityAvailable,
          continueSelling:variantNode.continueSelling?.value === 'true' ? true : false,
          variantProperties: variantPropertiesObject,
          standaloneVariant: variantNode.standaloneVariant?.value === 'true' ? true : false,
        };
      
    });

    const productSwatchBadge = product.swatchBadge === null ? null : product.swatchBadge.reference.fields;
    const swatchObject = productSwatchBadge === null ? null : {};
    if (productSwatchBadge) {
      productSwatchBadge.forEach(({ key, value }) => {
        swatchObject[key] = value;
      });
    }
    const productAccordionReferences = product.productAccordion?.references?.nodes;
    let accordionObjects = {};
    if (productAccordionReferences) {
      accordionObjects = productAccordionReferences.map((node) => {
        const resultObject = {};
        node.fields.forEach(({ key, value }) => {
          if (key === 'tab_html' || key === 'tab_label') {
            resultObject[key] = value;
          }
        });
        return resultObject;
      });
    }

    const multiSwatchReferences = product.multiSwatch?.references?.nodes;
    let multiSwatchObjects = {};
    if (multiSwatchReferences) {
      multiSwatchObjects = multiSwatchReferences.map((node) => {
        const resultObject = {};
        node.fields.forEach(({ key, value }) => {
          resultObject[key] = value;
        });
        return resultObject;
      });
    } else {
      multiSwatchObjects = null;
    }

    const optionPropsReferences = product.optionProps?.references?.nodes;
    let optionPropsObjects = {};
    if (optionPropsReferences) {
      optionPropsObjects = optionPropsReferences.map((node) => {
        const resultObject = {};
        node.fields.forEach(({ key, value }) => {
          resultObject[key] = value;
        });
        return resultObject;
      });
    } else {
      optionPropsObjects = null;
    }

    const transformedProduct = {
      name: product.title,
      type: product.productType,
      media: mediaObjects,
      handle: product.handle,
      options: product.options,
      variants,
      bundleHandle: product.bundleHandle ? product.bundleHandle.value : null,
      backStock: product.backStock ? true : false,
      tags: product.tags,
      availableForSale: product.availableForSale,
      swatchImage: product.swatchImage?.value,
      swatchOrder: product.swatchOrder?.value,
      disableSwatch: product.disableSwatch?.value,
      shortDescription: product.shortDescription?.value,
      swatchBadge: swatchObject,
      productAccordion: accordionObjects,
      multiSwatch: multiSwatchObjects,
      optionProps: optionPropsObjects,
    };
    console.log('transformedProduct5', transformedProduct);

    productsData[handle] = transformedProduct;
    return transformedProduct;
  } catch (error) {
    console.error('Error fetching product data:', error);
    throw error;
  }
};

async function fetchLimitedVariantInfo(variantId) {
  let globalVariantId = createGlobalId(variantId, 'ProductVariant');

  const VARIANT_PRICE_QUERY = /* GraphQL */ `
    query getVariantPrice($id: ID!, $country: CountryCode!) @inContext(country: $country) {
      node(id: $id) {
        ... on ProductVariant {
          id
          priceV2 {
            amount
            currencyCode
          }
          compareAtPriceV2 {
            amount
            currencyCode
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: VARIANT_PRICE_QUERY,
        variables: {
          id: globalVariantId,
          country: country,
        },
      }),
    });
    const jsonResponse = await response.json();

    const variantInfo = {
      id: variantId,
      price: jsonResponse.data.node.priceV2,
      compareAtPrice: jsonResponse.data.node.compareAtPriceV2,
    };
    return variantInfo;
  } catch (error) {
    console.error('Error fetching variant price:', error);
    throw error;
  }
}

const fetchHammockHandles = async (specialCollectionType) => {
  const PDP_SWATCH_QUERY = /* GraphQL */ `
    query getHammockHandles($type: String!) {
      products(first: 20, query: $type) {
        edges {
          node {
            handle
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: PDP_SWATCH_QUERY,
        variables: {
          type: `product_type:${specialCollectionType}`,
        },
      }),
    });
    const { data } = await response.json();
    const products = data.products.edges.map((edge) => edge.node);

    const hammockHandles = products.map((product) => product.handle);

    return hammockHandles;
  } catch (error) {
    console.error('Error fetching plp swatch data:', error);
    throw error;
  }
};

const fetchBundle = async (handle) => {
  if (productsData[handle]) {
    return productsData[handle];
  }

  const BUNDLE_DATA_QUERY = /* GraphQL */ `
    query getBundleByHandle($handle: String!, $country: CountryCode!) @inContext(country: $country) {
      productByHandle(handle: $handle) {
        id
        title
        options {
          name
          values
        }
        variants(first: 10) {
          edges {
            node {
              id
              title
              availableForSale
              priceV2 {
                amount
                currencyCode
              }
              compareAtPriceV2 {
                amount
                currencyCode
              }
            }
          }
        }
        images(first: 1) {
          edges {
            node {
              originalSrc
              altText
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: BUNDLE_DATA_QUERY,
        variables: {
          handle: handle,
          country: country,
        },
      }),
    });
    const jsonResponse = await response.json();

    if (jsonResponse.errors) {
      console.error('GraphQL errors:', jsonResponse.errors);
      throw new Error('GraphQL errors occurred');
    }

    const bundle = jsonResponse.data.productByHandle;
    if (!bundle) {
      console.error('Bundle not found for handle:', handle);
      throw new Error('Bundle not found');
    }

    const firstImage = bundle.images.edges[0]?.node;

    const transformedBundle = {
      id: createStandardId(bundle.id),
      title: bundle.title,
      firstImage: {
        originalSrc: firstImage?.originalSrc,
        altText: firstImage?.altText,
      },
      options: bundle.options,
      variants: bundle.variants.edges.map(({ node }) => ({
        id: createStandardId(node.id),
        disableSwatch: node.disableSwatch,
        title: node.title,
        price: node.priceV2.amount,
        availableForSale: node.availableForSale,
        currencyCode: node.priceV2.currencyCode,
        compareAtPrice: node.compareAtPriceV2 ? node.compareAtPriceV2.amount : null,
        formattedPrice: formatPrice(node.priceV2.currencyCode, node.priceV2.amount),
        formattedCompareAtPrice: node.compareAtPriceV2
          ? formatPrice(node.compareAtPriceV2.currencyCode, node.compareAtPriceV2.amount)
          : null,
        hasReducedPrice: node.compareAtPriceV2 && node.compareAtPriceV2.amount > node.priceV2.amount ? true : false,
      })),
    };
    
    productsData[handle] = transformedBundle;
    return transformedBundle;
  } catch (error) {
    console.error('Error fetching bundle data:', error);
    throw error;
  }
};

const fetchLineItem = async (variantId) => {
  let globalVariantId = createGlobalId(variantId, 'ProductVariant');

  const VARIANT_QUERY = /* GraphQL */ `
    query getVariant($variantId: ID!, $country: CountryCode!) @inContext(country: $country) {
      node(id: $variantId) {
        ... on ProductVariant {
          id
          title
          priceV2 {
            amount
            currencyCode
          }
          compareAtPriceV2 {
            amount
            currencyCode
          }
          quantityAvailable
          hasFreeGift: metafield(namespace: "custom", key: "gift_with_purchase") {
            value
          }
          product {
            shortTitle: metafield(namespace: "custom", key: "enhanced_product_title") {
              value
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: VARIANT_QUERY,
        variables: {
          variantId: globalVariantId,
          country: country,
        },
      }),
    });

    const data = await response.json();
    const variant = data?.data?.node;

    if (!variant) {
      throw new Error('Variant not found');
    }

    const lineItemData = {
      id: Number(variant.id.replace('gid://shopify/ProductVariant/', '')),
      title: variant.title,
      price: variant.priceV2,
      compareAtPrice: variant.compareAtPriceV2,
      quantityAvailable: variant.quantityAvailable,
      hasFreeGift: variant.hasFreeGift?.value === 'true' ? true : false,
      shortTitle: variant.product?.shortTitle?.value,
    };

    return lineItemData;
  } catch (error) {
    console.error('Error fetching line item data:', error);
    throw error;
  }
};

const fetchProductCard = async (productId) => {
  if (productsData[productId]) {
    return productsData[productId];
  }
  let globalProductId = createGlobalId(productId, 'Product');
  if (!globalProductId) return;
  const PRODUCT_CARD_QUERY = /* GraphQL */ `
    query getProductCard($productId: ID!, $country: CountryCode!) @inContext(country: $country) {
      node(id: $productId) {
        ... on Product {
          id
          handle
          description
          title
          productType
          availableForSale
          options {
            name
            values
          }
          variants(first: 250) {
            edges {
              node {
                id
                title
                availableForSale
                variantSelectorImage: metafield(namespace: "custom", key: "variant_selector_image") {
                  reference {
                    ... on MediaImage {
                      image {
                        originalSrc
                      }
                    }
                  }
                }
                variantSubtext: metafield(namespace: "custom", key: "variant_subtext") {
                  value
                }
                variantOffPrice: metafield(namespace: "custom", key: "off_price") {
                  value
                }
                variantAmPrice: metafield(namespace: "custom", key: "discount_amount") {
                  value
                }
                variantTag: metafield(namespace: "custom", key: "variant_tag") {
                  value
                }
                selectedOptions {
                  name
                  value
                }
                priceV2 {
                  amount
                  currencyCode
                }
                compareAtPriceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          media(first: 7) {
            edges {
              node {
                ... on MediaImage {
                  image {
                    originalSrc
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
          tags
          enhancedTitle: metafield(namespace: "custom", key: "enhanced_product_title") {
            value
          }
          cardDescription: metafield(namespace: "custom", key: "product_card_description") {
            value
          }
          productOffPrice: metafield(namespace: "custom", key: "off_price_product") {
            value
          }
          disableQuickAdd: metafield(namespace: "custom", key: "disable_quick_add") {
            value
          }
          disableSwatch: metafield(namespace: "prc", key: "disable_swatch") {
            value
          }
          starRatingSnippet: metafield(namespace: "custom", key: "StarRatingSnippet") {
            value
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: PRODUCT_CARD_QUERY,
        variables: {
          productId: globalProductId,
          country: country,
        },
      }),
    });
    const cardResponse = await response.json();
    const product = cardResponse.data.node;

    const PDiscountValue = product.productOffPrice?.value * window.Shopify.currency.rate;
      
    const transformedProduct = {
      id: product.id,
      handle: product.handle,
      productType: product.productType,
      description: product.description,
      title: product.title,
      availableForSale: product.availableForSale,
      priceRangeFormatted: product.priceRange,
      compareAtPriceRangeFormatted: product.compareAtPriceRange,
      options: product.options,
      
      variants: product.variants.edges.map((edge) => {
        const variant = edge.node;

        const VDiscountValue = variant.variantOffPrice?.value * window.Shopify.currency.rate;
        const moneyDiscountValue = isNaN(VDiscountValue) != true ? VDiscountValue : PDiscountValue;

        const moneyDiscountValueTR = VDiscountValue != 0 ? true : false;
        const price = isNaN(moneyDiscountValue) != true ? variant.priceV2.amount - moneyDiscountValue : variant.priceV2.amount;
        const compareAtPrice = moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? variant.priceV2.amount : variant.compareAtPriceV2 ? variant.compareAtPriceV2.amount : null;
        const hasReducedPrice = moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? true
                : variant.compareAtPriceV2 && variant.compareAtPriceV2.amount > variant.priceV2.amount ? true : false;
    
        const formattedPrice = isNaN(moneyDiscountValue) != true ? formatPrice(variant.priceV2.currencyCode, variant.priceV2.amount - moneyDiscountValue)
          : formatPrice(variant.priceV2.currencyCode, variant.priceV2.amount);
        
        const formattedCompareAtPrice = isNaN(moneyDiscountValue) != true ? formatPrice(variant.priceV2.currencyCode, variant.priceV2.amount)
          : variant.compareAtPriceV2 ? formatPrice(variant.compareAtPriceV2.currencyCode, variant.compareAtPriceV2.amount) : null;

        const variantSelectorImage = variant.variantSelectorImage
          ? variant.variantSelectorImage?.reference.image.originalSrc
          : null;
        return {
          ...variant,
          price: price,
          compareAtPrice: compareAtPrice,
          hasReducedPrice: hasReducedPrice,
          formattedPrice: formattedPrice,
          formattedCompareAtPrice: formattedCompareAtPrice,
          variantSelectorImage: variantSelectorImage,
          variantTag: variant.variantTag?.value
        };
      }),
      images: product.media.edges.map((edge) => edge.node.image),
      tags: product.tags,
      reviewSnippet: product.starRatingSnippet?.value,
      enhancedTitle: product.enhancedTitle?.value,
      cardDescription: product.cardDescription?.value,
      disableQuickAdd: product.disableQuickAdd?.value,
      disableSwatch: product.disableSwatch?.value
    };
    
    console.log("MOPP"+transformedProduct);    
    productsData[productId] = transformedProduct;

    return transformedProduct;
  } catch (error) {
    console.error('Error fetching product card data:', error);
    throw error;
  }
};

const fetchEnhancedProductCard = async (specialCollectionType) => {
  const ENHANCED_PRODUCT_CARD_QUERY = /* GraphQL */ `
    query getEnhancedProductCards($type: String!, $country: CountryCode!) @inContext(country: $country) {
      products(first: 15, query: $type) {
        edges {
          node {
            id
            handle
            description
            title
            productType
            availableForSale
            options {
              name
              values
            }
            variants(first: 250) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  disableSwatch: metafield(namespace: "prc", key: "disable_swatch") {
                    value
                  }
                  variantSelectorImage: metafield(namespace: "custom", key: "variant_selector_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          originalSrc
                        }
                      }
                    }
                  }
                  variantSubtext: metafield(namespace: "custom", key: "variant_subtext") {
                    value
                  }
                  variantOffPrice: metafield(namespace: "custom", key: "off_price") {
                    value
                  }
                  selectedOptions {
                    name
                    value
                  }
                  priceV2 {
                    amount
                    currencyCode
                  }
                  compareAtPriceV2 {
                    amount
                    currencyCode
                  }
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            media(first: 5) {
              edges {
                node {
                  ... on MediaImage {
                    image {
                      originalSrc
                      altText
                      width
                      height
                    }
                  }
                }
              }
            }
            tags
            enhancedTitle: metafield(namespace: "custom", key: "enhanced_product_title") {
              value
            }
            cardDescription: metafield(namespace: "custom", key: "product_card_description") {
              value
            }
            productOffPrice: metafield(namespace: "custom", key: "off_price_product") {
              value
            }
            swatchImage: metafield(namespace: "custom", key: "swatch_image_reference") {
              value
            }
            swatchOrder: metafield(namespace: "custom", key: "swatch_order") {
              value
            }
            disableQuickAdd: metafield(namespace: "custom", key: "disable_quick_add") {
              value
            }
            disableSwatch: metafield(namespace: "prc", key: "disable_swatch") {
              value
            }
            starRatingSnippet: metafield(namespace: "custom", key: "StarRatingSnippet") {
              value
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: ENHANCED_PRODUCT_CARD_QUERY,
        variables: {
          type: `product_type:${specialCollectionType}`,
          country: country,
        },
      }),
    });

    const { data } = await response.json();
    const products = data.products.edges.map((edge) => edge.node);
    
    const enhancedProductsData = products.map((product) => ({
      id: product.id,
      handle: product.handle,
      description: product.description,
      title: product.title,
      productType: product.productType,
      availableForSale: product.availableForSale,
      options: product.options,
      variants: product.variants.edges.map((edge) => {
        const variant = edge.node;

        const PDiscountValue = product.productOffPrice?.value * window.Shopify.currency.rate;

        const VDiscountValue = variant.variantOffPrice?.value * window.Shopify.currency.rate;
        const moneyDiscountValue = isNaN(VDiscountValue) != true ? VDiscountValue : PDiscountValue;

        const moneyDiscountValueTR = VDiscountValue != 0 ? true : false;
        const price = isNaN(moneyDiscountValue) != true ? variant.priceV2.amount - moneyDiscountValue : variant.priceV2.amount;
        const compareAtPrice = moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? variant.priceV2.amount : variant.compareAtPriceV2 ? variant.compareAtPriceV2.amount : null;
        const hasReducedPrice = moneyDiscountValueTR != false && isNaN(moneyDiscountValue) != true ? true
                : variant.compareAtPriceV2 && variant.compareAtPriceV2.amount > variant.priceV2.amount ? true : false;
        
        const formattedPrice = isNaN(moneyDiscountValue) != true ? formatPrice(variant.priceV2.currencyCode, variant.priceV2.amount - moneyDiscountValue)
          : formatPrice(variant.priceV2.currencyCode, variant.priceV2.amount);
        
        const formattedCompareAtPrice = isNaN(moneyDiscountValue) != true ? formatPrice(variant.priceV2.currencyCode, variant.priceV2.amount)
          : variant.compareAtPriceV2 ? formatPrice(variant.compareAtPriceV2.currencyCode, variant.compareAtPriceV2.amount) : null;
        
        const variantSelectorImage = variant.variantSelectorImage
          ? variant.variantSelectorImage?.reference.image.originalSrc
          : null;
        return {
          ...variant,
          price: price,
          compareAtPrice: compareAtPrice,
          hasReducedPrice: hasReducedPrice,
          formattedPrice: formattedPrice,
          formattedCompareAtPrice: formattedCompareAtPrice,
          variantSelectorImage: variantSelectorImage,
        };
      }),
      priceRangeFormatted: product.priceRange,
      compareAtPriceRangeFormatted: product.compareAtPriceRange,
      images: product.media.edges.map((edge) => edge.node.image),
      tags: product.tags,
      enhancedTitle: product.enhancedTitle?.value,
      cardDescription: product.cardDescription?.value,
      swatchOrder: product.swatchOrder?.value,
      swatchImage: product.swatchImage?.value,
      disableQuickAdd: product.disableQuickAdd?.value,
      disableSwatch: product.disableSwatch?.value,
      starRatingSnippet: product.starRatingSnippet?.value,
    }));

    return enhancedProductsData;
  } catch (error) {
    console.error('Error fetching enhanced product card data:', error);
    throw error;
  }
};

const fetchCollection = async (collectionHandle, numOfProducts = 100) => {
  const COLLECTION_QUERY = /* GraphQL */ `
    query getCollectionByHandle($handle: String!, $numOfProducts: Int!) {
      collectionByHandle(handle: $handle) {
        title
        handle
        products(first: $numOfProducts) {
          edges {
            node {
              id
              handle
              productType
              onlineStoreUrl
              starRatingSnippet: metafield(namespace: "okendo", key: "StarRatingSnippet") {
                value
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: COLLECTION_QUERY,
        variables: {
          handle: collectionHandle,
          numOfProducts: numOfProducts,
        },
      }),
    });

    const { data } = await response.json();
    const collection = data.collectionByHandle;

    const products = collection.products.edges.map((edge) => ({
      id: edge.node.id.split('/').pop(),
      handle: edge.node.handle,
      type: edge.node.productType,
      url: edge.node.onlineStoreUrl,
      starRatingSnippet: edge.node.starRatingSnippet?.value,
    }));

    const transformedCollection = {
      title: collection.title,
      handle: collection.handle,
      url: `/${collection.handle}`,
      products: products,
    };

    return transformedCollection;
  } catch (error) {
    console.error('Error fetching collection:', error);
    throw error;
  }
};

async function fetchFilters(collectionHandle) {
  const query = `
    query CollectionAvailableFilters($handle: String!) {
      collection(handle: $handle) {
        handle
        omit_filters: metafield(namespace: "custom", key: "omit_filters") {
          value
        }
        products(first: 12) {
          filters {
            id
            label
            type
            values {
              id
              label
              count
              input
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query,
        variables: { handle: collectionHandle },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const { data } = await response.json();

    let omitFilters = [];
    if (data.collection.omit_filters && data.collection.omit_filters.value) {
      omitFilters = JSON.parse(data.collection.omit_filters.value);
    }

    const filters = data.collection.products.filters.filter((filter) => !omitFilters.includes(filter.id));

    return filters;
  } catch (error) {
    console.error('Error fetching collection available filters:', error);
    throw error;
  }
}

const fetchFilteredCollection = async (
  collectionHandle,
  afterCursor = null,
  filtersArray,
  numOfProducts,
  sortField,
  reverse,
) => {
  const FILTERED_PRODUCTS_QUERY = /* GraphQL */ `
    query CollectionByHandleWithFilters(
      $handle: String!
      $afterCursor: String
      $numOfProducts: Int!
      $sortField: ProductCollectionSortKeys
      $reverse: Boolean
      $filters: [ProductFilter!]
    ) {
      collectionByHandle(handle: $handle) {
        displaySecondVariant: metafield(namespace: "custom", key: "display_second_variant") {
          value
        }
        products(
          first: $numOfProducts
          after: $afterCursor
          filters: $filters
          sortKey: $sortField
          reverse: $reverse
        ) {
          pageInfo {
            hasPreviousPage
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              disableSwatch: metafield(namespace: "prc", key: "disable_swatch") {
                value
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: FILTERED_PRODUCTS_QUERY,
        variables: {
          handle: collectionHandle,
          afterCursor: afterCursor,
          numOfProducts: numOfProducts,
          filters: filtersArray,
          sortField: sortField,
          reverse: reverse,
        },
      }),
    });
    const fetchResponse = await response.json();
    const { data } = fetchResponse;
    const pageInfo = data.collectionByHandle.products.pageInfo;
    const displaySecondVariant = data.collectionByHandle.displaySecondVariant;
    const products = data.collectionByHandle.products.edges.map((edge) => ({
      id: edge.node.id,
      handle: edge.node.handle,
      disableSwatch: edge.node.disableSwatch?.value
    }));
    return {
      handle: collectionHandle,
      displaySecondVariant,
      products,
      pageInfo,
    };
  } catch (error) {
    console.error('Error fetching filtered products:', error);
    throw error;
  }
};

const fetchBlogsCategory = async (hidden_blogs) => {
  const filterQuery = `query:"-handle:'${hidden_blogs}'"`;
  const BLOGS_DATA_QUERY = /* GraphQL */ `
    {
      blogs(first: 10, ${filterQuery},reverse: true) {
        edges {
          node {
            title
            handle
          }
        }
      }
    }
  `;

  const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    body: JSON.stringify({
      query: BLOGS_DATA_QUERY,
    }),
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
    },
  });

  const {
    data: { blogs },
  } = await response.json();

  return blogs.edges.map((edge) => {
    return {
      handle: edge.node.handle,
      title: edge.node.title,
    };
  });
};

const fetchBlogs = async (blogsPerPage, blogHandle, cursor) => {
  
  // FetchAll if 'all' handle
  if(blogHandle === 'all') {
    return fetchAllBlogs(blogsPerPage, cursor);
  }

  const cursorQuery = cursor ? `after: "${cursor}"` : '';
  const BLOGS_DATA_QUERY = /* GraphQL */
    `{
    blog(handle:"${blogHandle}"){
        handle
        title
        articles(first : ${blogsPerPage},${cursorQuery},reverse: true){
          nodes{
            title
            handle
            image{
              altText
              url
            }
            onlineStoreUrl
            publishedAt
          }
          pageInfo{
            endCursor,
            hasNextPage
            hasPreviousPage
            startCursor
          }
        }
      }
    }`;

  const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    body: JSON.stringify({
      query: BLOGS_DATA_QUERY,
    }),
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
    },
  });

  const {
    data: { blog },
  } = await response.json();

  const { pageInfo } = blog.articles;

  const articles = blog.articles.nodes.map((node) => {
    return {
      blogHandle: blog.title,
      title: node.title,
      articleHandle: node.handle,
      imageSrc: node.image?.url,
      altText: node.image?.altText,
      url: node.onlineStoreUrl,
      publishedAt: node.publishedAt,
    };
  });

  return { articles, ...pageInfo };
};

const fetchAllBlogs = async (blogsPerPage, cursor) => {
  const cursorQuery = cursor ? `after: "${cursor}"` : '';
  const BLOGS_DATA_QUERY = /* GraphQL */
    `{
        articles(first : ${blogsPerPage},${cursorQuery},reverse: true){
          nodes{
            blog {
              title
              handle
            }
            title
            handle
            image{
              altText
              url
            }
            onlineStoreUrl
            publishedAt
          }
          pageInfo{
            endCursor,
            hasNextPage
            hasPreviousPage
            startCursor
          }
        }
      }
      `;

  const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    body: JSON.stringify({
      query: BLOGS_DATA_QUERY,
    }),
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
    },
  });

  const {
    data: { articles },
  } = await response.json();

  console.log('kya bay', articles);

  const { pageInfo } = articles;

  const _articles = articles.nodes.map((node) => {
    return {
      blogHandle: node.blog.title,
      title: node.title,
      articleHandle: node.handle,
      imageSrc: node.image?.url,
      altText: node.image?.altText,
      url: node.onlineStoreUrl,
      publishedAt: node.publishedAt,
    };
  });

  console.log('abay chicha', _articles);

  return { articles: _articles, ...pageInfo };
}